import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';

//sx={{ maxHeight: 'calc(100vh - 275px)', minHeight: 300 }}

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
    switch (align) {
        case 'center':
            return 'center';
        case 'right':
            return 'flex-end';
        default:
            return 'flex-start';
    }
};
interface ParameterTableProps<T extends object> {
    instance: TableInstance<T>;
    title: string;
}
export function ParameterTable<T extends object>({ instance, title }: ParameterTableProps<T>) {
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = instance;
    const headers = headerGroups.map(headerGroup => (
        <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
                <TableCell
                    sx={{ backgroundColor: 'background.paper' }}
                    align={column.align}
                    {...column.getHeaderProps({
                        style: {
                            width: column.width,
                            minWidth: column.minWidth,
                        },
                    })}
                >
                    <Box
                        component='span'
                        width={1}
                        display='flex'
                        justifyContent='center'
                        {...column.getSortByToggleProps({
                            style: { justifyContent: getHeaderFlexAlignment(column.align) },
                        })}
                        title={column.title}
                    >
                        {column.render('Header')}
                        {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
                    </Box>
                </TableCell>
            ))}
        </TableRow>
    ));
    const tableRows = rows.map((row, i) => {
        prepareRow(row);
        const { key, ...rest } = row.getRowProps();
        return <ParameterTableRow key={key} row={row} {...rest} />;
    });
    return (
        <>
            <Toolbar disableGutters sx={{ pl: 2 }}>
                <Typography variant='h5'>{title}</Typography>
            </Toolbar>
            <TableContainer>
                <Table size='small' stickyHeader {...getTableProps()}>
                    <TableHead>{headers}</TableHead>
                    <TableBody {...getTableBodyProps()}>{tableRows}</TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

interface ParameterTableRowProps<T extends object> extends TableRowProps {
    row: Row<T>;
}

function ParameterTableRow<T extends object>({ row, ...props }: ParameterTableRowProps<T>) {
    return (
        <TableRow {...props}>
            {row.cells.map(cell => (
                <TableCell align={cell.column.align} {...cell.getCellProps()}>
                    {cell.render('Cell', {})}
                </TableCell>
            ))}
        </TableRow>
    );
}

interface FormSubmitButtonsProps {
    resetForm(): void;
}

export function FormSubmitButtons({ resetForm }: FormSubmitButtonsProps) {
    const {
        formState: { isDirty, isSubmitting },
    } = useFormContext();

    return isDirty ? (
        <Box>
            <Button disabled={isSubmitting} type='submit' color='secondary' variant='contained'>
                save
            </Button>
            <Button disabled={isSubmitting} onClick={resetForm} variant='outlined'>
                reset
            </Button>
        </Box>
    ) : null;
}
